// @ts-check
// Passo "Remetente e assunto" do assistente da campanha de e-mail (spec/email.md seções 10.3 e 19.7): de onde sai (o
// endereço de envio da escola ou a conta conectada do próprio membro), o nome do remetente, o assunto e o texto de
// prévia com as variáveis, a marcação UTM dos links do site e a campanha de mídia ligada. Controlado pelo assistente.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { listConnectedAccounts } from '../../../data/calendar.js';
import { findManyRecords } from '../../../data/rest.js';
import { objectsByNameSingular } from '../../../data/metadata-store.js';
import { reviewSubjectLine } from '../../../../../shared/email/campaign-review.js';
import { LightIconButton } from '../../../ui/button.js';
import { Icon } from '../../../ui/icon.js';
import { Toggle } from '../../../ui/feedback.js';
import { VariablePicker, useEmailVariableCatalog } from '../../email-design/variable-picker.js';
import { useEmailBranding } from '../../email-design/email-branding.js';
import { t } from '../../../i18n/index.js';

/**
 * @typedef {{ name: string, senderType: 'INSTITUTION' | 'CONNECTED_ACCOUNT', connectedAccountId: string | null, fromName: string,
 *   utmEnabled: boolean, mediaCampaignId: string | null }} SenderValue
 */

const MAXIMUM_LINE_LENGTH = 200;

/**
 * Linha de texto com o seletor de variáveis (assunto e texto de prévia).
 * @param {{ id: string, label: string, value: string, onChange: (value: string) => void, hint?: string, warning?: string | null, readOnly?: boolean, placeholder?: string }} props
 */
function VariableLineField({ id, label, value, onChange, hint, warning, readOnly = false, placeholder }) {
  const catalog = useEmailVariableCatalog();
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const anchorRef = useRef(/** @type {HTMLElement | null} */ (null));
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  /** @param {string} expression */
  const insert = (expression) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    onChange(`${value.slice(0, start)}${expression}${value.slice(end)}`.slice(0, MAXIMUM_LINE_LENGTH));
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + expression.length, start + expression.length);
    });
  };
  return html`<div class="ec-field">
    <label class="ec-field-label" for=${id}>${label}</label>
    <div class="ec-line-input" ref=${anchorRef}>
      <input ref=${inputRef} id=${id} class="text-input" value=${value} maxLength=${MAXIMUM_LINE_LENGTH} readOnly=${readOnly} placeholder=${placeholder}
        onInput=${(/** @type {Event} */ event) => onChange(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      ${readOnly ? null : html`<${LightIconButton} icon="IconBraces" ariaLabel=${t('emailCampaigns.sender.insertVariable')} onClick=${() => setIsPickerOpen((open) => !open)} />`}
    </div>
    ${warning ? html`<span class="input-hint" data-danger="true">${warning}</span>` : hint ? html`<span class="input-hint">${hint}</span>` : null}
    <${VariablePicker} anchorRef=${anchorRef} isOpen=${isPickerOpen} onClose=${() => setIsPickerOpen(false)} catalog=${catalog} placement="bottom-end"
      excludePaths=${['unsubscribeUrl']} onSelect=${(/** @type {string} */ expression) => insert(expression)} />
  </div>`;
}

/**
 * @param {{ value: SenderValue, onChange: (patch: Partial<SenderValue>) => void, subject: string, preheader: string,
 *   onLinesChange: (patch: { subject?: string, preheader?: string }) => void, sendingAddress: string, replyToAddress: string, readOnly?: boolean }} props
 */
export function SenderStep({ value, onChange, subject, preheader, onLinesChange, sendingAddress, replyToAddress, readOnly = false }) {
  const branding = useEmailBranding();
  const [accounts, setAccounts] = useState(/** @type {import('../../../data/calendar.js').ConnectedAccount[]} */ ([]));
  const [mediaCampaigns, setMediaCampaigns] = useState(/** @type {Array<{ id: string, name: string, channel: string | null }>} */ ([]));
  const mediaObject = objectsByNameSingular.value.get('campaign');

  useEffect(() => {
    listConnectedAccounts().then((result) => setAccounts((result.accounts ?? []).filter((account) => account.isMine && !account.authFailedAt))).catch(() => setAccounts([]));
    if (mediaObject) {
      findManyRecords(mediaObject, { limit: 100, depth: 0 })
        .then((page) => setMediaCampaigns(page.records.map((record) => ({ id: String(record.id), name: String(record.name ?? ''), channel: /** @type {string | null} */ (record.channel ?? null) }))))
        .catch(() => setMediaCampaigns([]));
    }
  }, []);

  const subjectChecks = reviewSubjectLine({ subject, preheader });
  const subjectWarning = subjectChecks.find((check) => check.code === 'SUBJECT_LONG') ? t('emailCampaigns.sender.subjectLong') : null;
  const selectedMedia = mediaCampaigns.find((item) => item.id === value.mediaCampaignId);

  return html`<div class="ec-step ec-sender-step" data-testid="email-campaign-sender-step">
    <section class="ec-section">
      <label class="ec-field">
        <span class="ec-field-label">${t('emailCampaigns.wizard.name')}</span>
        <input class="text-input" value=${value.name} maxLength=${120} readOnly=${readOnly} data-testid="email-campaign-name"
          onInput=${(/** @type {Event} */ event) => onChange({ name: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
        <span class="input-hint">${t('emailCampaigns.wizard.nameHint')}</span>
      </label>
    </section>

    <section class="ec-section">
      <h3 class="ec-section-title">${t('emailCampaigns.sender.title')}</h3>
      <div class="ec-radio-cards" role="radiogroup" aria-label=${t('emailCampaigns.sender.title')}>
        <button type="button" role="radio" class="ec-radio-card" aria-checked=${value.senderType === 'INSTITUTION' ? 'true' : 'false'} disabled=${readOnly}
          onClick=${() => onChange({ senderType: 'INSTITUTION', connectedAccountId: null })}>
          <${Icon} name="IconBuildingCommunity" size=${18} />
          <span class="ec-radio-card-texts">
            <strong>${t('emailCampaigns.sender.institution')}</strong>
            <span class="ec-muted">${t('emailCampaigns.sender.institutionHint', { address: sendingAddress, replyTo: replyToAddress })}</span>
          </span>
        </button>
        <button type="button" role="radio" class="ec-radio-card" aria-checked=${value.senderType === 'CONNECTED_ACCOUNT' ? 'true' : 'false'}
          disabled=${readOnly || accounts.length === 0}
          onClick=${() => onChange({ senderType: 'CONNECTED_ACCOUNT', connectedAccountId: value.connectedAccountId ?? accounts[0]?.id ?? null })}>
          <${Icon} name="IconUser" size=${18} />
          <span class="ec-radio-card-texts">
            <strong>${t('emailCampaigns.sender.account')}</strong>
            <span class="ec-muted">${accounts.length === 0 ? t('emailCampaigns.sender.noAccounts') : t('emailCampaigns.sender.accountHint')}</span>
          </span>
        </button>
      </div>
      ${value.senderType === 'CONNECTED_ACCOUNT' && accounts.length > 0
        ? html`<label class="ec-field">
            <span class="ec-field-label">${t('emailCampaigns.sender.accountPick')}</span>
            <select class="text-input" value=${value.connectedAccountId ?? ''} disabled=${readOnly}
              onChange=${(/** @type {Event} */ event) => onChange({ connectedAccountId: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
              ${accounts.map((account) => html`<option key=${account.id} value=${account.id}>${account.handle}</option>`)}
            </select>
          </label>`
        : null}
      <label class="ec-field">
        <span class="ec-field-label">${t('emailCampaigns.sender.fromName')}</span>
        <input class="text-input" value=${value.fromName} maxLength=${80} readOnly=${readOnly} placeholder=${branding.settings?.fromName || branding.institutionName}
          onInput=${(/** @type {Event} */ event) => onChange({ fromName: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
        <span class="input-hint">${t('emailCampaigns.sender.fromNameHint', { name: branding.settings?.fromName || branding.institutionName })}</span>
      </label>
    </section>

    <section class="ec-section">
      <h3 class="ec-section-title">${t('emailCampaigns.sender.subjectTitle')}</h3>
      <${VariableLineField} id="ec-subject" label=${t('emailCampaigns.sender.subject')} value=${subject} readOnly=${readOnly}
        hint=${t('emailCampaigns.sender.subjectHint')} warning=${subjectWarning} onChange=${(/** @type {string} */ text) => onLinesChange({ subject: text })} />
      <${VariableLineField} id="ec-preheader" label=${t('emailCampaigns.sender.preheader')} value=${preheader} readOnly=${readOnly}
        hint=${t('emailCampaigns.sender.preheaderHint')} onChange=${(/** @type {string} */ text) => onLinesChange({ preheader: text })} />
    </section>

    <section class="ec-section">
      <h3 class="ec-section-title">${t('emailCampaigns.sender.trackingTitle')}</h3>
      <div class="ec-toggle-row">
        <${Toggle} value=${value.utmEnabled} disabled=${readOnly} ariaLabel=${t('emailCampaigns.sender.utm')} onChange=${(/** @type {boolean} */ enabled) => onChange({ utmEnabled: enabled })} />
        <div><strong>${t('emailCampaigns.sender.utm')}</strong><p class="input-hint">${t('emailCampaigns.sender.utmHint')}</p></div>
      </div>
      ${mediaObject
        ? html`<label class="ec-field">
            <span class="ec-field-label">${t('emailCampaigns.sender.mediaCampaign')}</span>
            <select class="text-input" value=${value.mediaCampaignId ?? ''} disabled=${readOnly}
              onChange=${(/** @type {Event} */ event) => onChange({ mediaCampaignId: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
              <option value="">${t('emailCampaigns.sender.mediaCampaignNone')}</option>
              ${mediaCampaigns.map((item) => html`<option key=${item.id} value=${item.id}>${item.name}</option>`)}
            </select>
            <span class="input-hint" data-danger=${selectedMedia?.channel && selectedMedia.channel !== 'EMAIL' ? 'true' : undefined}>
              ${selectedMedia?.channel && selectedMedia.channel !== 'EMAIL' ? t('emailCampaigns.sender.mediaCampaignChannel') : t('emailCampaigns.sender.mediaCampaignHint')}
            </span>
          </label>`
        : null}
    </section>
  </div>`;
}
